import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView, TextInput, KeyboardAvoidingView, Platform, Pressable, ActivityIndicator, RefreshControl, Image } from 'react-native';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import ThemedText from '../components/common/ThemedText';
import FeaturedFeedPost from '../components/feed/FeaturedFeedPost';
import PollPost from '../components/feed/PollPost';
import LinkPost from '../components/feed/LinkPost';
import { HomeStackParamList } from '../navigation/HomeNavigator';
import { Post, getComments, addComment, Comment, likePost, unlikePost, voteOnPoll, deletePost, deleteComment } from '../lib/api/feed';
import { storage } from '../utils/storage';
import { getTimeAgo } from '../utils/timeAgo';
import { Alert } from '../utils/toast.native';

type PostDetailRouteProp = RouteProp<HomeStackParamList, 'PostDetail'>;
type PostDetailNavigationProp = StackNavigationProp<HomeStackParamList, 'PostDetail'>;

const PostDetailScreen: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const route = useRoute<PostDetailRouteProp>();
  const navigation = useNavigation<PostDetailNavigationProp>();
  const { post: initialPost } = route.params;

  const [post, setPost] = useState<Post>(initialPost);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadComments = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await getComments(post.id);
      setComments(response.comments);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [post.id]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleAddComment = async () => {
    if (!commentText.trim()) return;

    try {
      setSubmitting(true);
      const token = await storage.getToken();
      if (!token) return;

      const result = await addComment(token, post.id, commentText.trim());
      setComments(prev => [result.comment, ...prev]);
      setCommentText('');
      
      // Update comment count
      setPost(prev => ({
        ...prev,
        commentsCount: prev.commentsCount + 1,
      }));
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    Alert.alert(
      'Delete Comment',
      'Are you sure you want to delete this comment?',
      [
        {
          text: 'Cancel',
          style: 'cancel'
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const token = await storage.getToken();
              if (!token) return;

              await deleteComment(token, post.id, commentId);
              setComments(prev => prev.filter(comment => comment.id !== commentId));
              setPost(prev => ({
                ...prev,
                commentsCount: prev.commentsCount > 0 ? prev.commentsCount - 1 : 0,
              }));
            } catch (error) {
              console.error('Error deleting comment:', error);
            }
          }
        }
      ]
    );
  };

  const handleLike = async () => {
    try {
      const token = await storage.getToken();
      if (!token) return;

      const isLiked = post.isLiked;
      
      // Optimistic update
      setPost(prev => ({
        ...prev,
        isLiked: !isLiked,
        likesCount: isLiked ? prev.likesCount - 1 : prev.likesCount + 1,
      }));

      if (isLiked) {
        await unlikePost(token, post.id);
      } else {
        await likePost(token, post.id);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert on error
      setPost(prev => ({
        ...prev,
        isLiked: post.isLiked,
        likesCount: post.likesCount,
      }));
    }
  };

  const handleVote = async (optionId: string) => {
    try {
      const token = await storage.getToken();
      if (!token) return;

      const result = await voteOnPoll(token, post.id, optionId);
      setPost(prev => ({
        ...prev,
        poll: result.poll,
      }));
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const token = await storage.getToken();
      if (!token) return;

      await deletePost(token, post.id);
      navigation.goBack();
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleUserPress = (userId: string) => {
    // Navigate to root Profile screen
    (navigation as any).navigate('Profile', { userId });
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const renderPost = () => {
    if (post.type === 'text' || post.type === 'image' || post.type === 'video') {
      return (
        <FeaturedFeedPost
          post={{
            id: post.id,
            userId: post.userId,
            user: post.user.name || 'Anonymous',
            location: post.user.profession || '',
            avatar: post.user.photo || undefined,
            caption: post.text || '',
            image: post.imageUrl || undefined,
            video: post.videoUrl || undefined,
            likes: formatNumber(post.likesCount),
            comments: formatNumber(post.commentsCount),
            isLiked: post.isLiked,
            createdAt: post.createdAt,
          }}
          currentUserId={user?.userId}
          onLike={handleLike}
          onDelete={handleDelete}
          onUserPress={handleUserPress}
        />
      );
    } else if (post.type === 'poll' && post.poll) {
      return (
        <PollPost
          post={{
            id: post.id,
            userId: post.userId,
            user: post.user.name || 'Anonymous',
            location: post.user.profession || '',
            avatar: post.user.photo || undefined,
            caption: post.poll.question,
            pollOptions: post.poll.options.map(opt => ({
              id: opt.id,
              text: opt.text,
              votes: opt.votesCount,
            })),
            totalVotes: post.poll.totalVotes,
            likes: formatNumber(post.likesCount),
            comments: formatNumber(post.commentsCount),
            userVote: post.poll.userVote,
            isLiked: post.isLiked,
            createdAt: post.createdAt,
          }}
          currentUserId={user?.userId}
          onVote={handleVote}
          onLike={handleLike}
          onDelete={handleDelete}
          onUserPress={handleUserPress}
        />
      );
    } else if (post.type === 'link' && post.linkUrl) {
      return (
        <LinkPost
          post={{
            id: post.id,
            userId: post.userId,
            user: post.user.name || 'Anonymous',
            location: post.user.profession || '',
            avatar: post.user.photo || undefined,
            caption: post.text || '',
            link: post.linkUrl,
            likes: formatNumber(post.likesCount),
            comments: formatNumber(post.commentsCount),
            isLiked: post.isLiked,
            createdAt: post.createdAt,
          }}
          currentUserId={user?.userId}
          onLike={handleLike}
          onDelete={handleDelete}
          onUserPress={handleUserPress}
        />
      );
    }
    return null;
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <Pressable onPress={() => navigation.goBack()} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </Pressable>
        <ThemedText variant="semibold" style={styles.headerTitle}>Post</ThemedText>
        <View style={{ width: 24 }} />
      </View>

      <KeyboardAvoidingView 
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
        <ScrollView
          style={styles.content}
          contentContainerStyle={{ paddingBottom: 20 }}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => loadComments(true)}
              tintColor={theme.accent}
            />
          }
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
        >
          {/* Post */}
          {renderPost()}

          {/* Comments Section */}
          <View style={[styles.commentsSection, { borderTopColor: theme.border }]}>
            <ThemedText variant="semibold" style={styles.commentsTitle}>
              Comments ({post.commentsCount})
            </ThemedText>

            {loading && comments.length === 0 ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color={theme.accent} />
              </View>
            ) : comments.length === 0 ? (
              <View style={styles.emptyContainer}>
                <ThemedText style={[styles.emptyText, { color: theme.textTertiary }]}>
                  No comments yet. Be the first to comment!
                </ThemedText>
              </View>
            ) : (
              comments.map((comment) => {
                const isOwner = user?.userId === comment.userId;
                return (
                  <View key={comment.id} style={[styles.comment, { borderBottomColor: theme.border }]}>
                    <View style={styles.commentHeader}>
                      <Pressable onPress={() => handleUserPress(comment.userId)} style={{ flex: 1 }}>
                        <View style={styles.commentUser}>
                          {comment.user.photo ? (
                            <Image source={{ uri: comment.user.photo }} style={styles.commentAvatar} />
                          ) : (
                            <View style={[styles.commentAvatar, { backgroundColor: theme.backgroundSecondary }]}>
                              <Ionicons name="person" size={16} color={theme.textSecondary} />
                            </View>
                          )}
                          <View style={styles.commentUserInfo}>
                            <ThemedText variant="semibold" style={styles.commentUserName}>
                              {comment.user.name || 'Anonymous'}
                            </ThemedText>
                            <ThemedText style={[styles.commentTime, { color: theme.textTertiary }]}>
                              {getTimeAgo(comment.createdAt)}
                            </ThemedText>
                          </View>
                        </View>
                      </Pressable>
                      {isOwner && (
                        <Pressable
                          onPress={() => handleDeleteComment(comment.id)}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                          style={styles.commentDeleteButton}
                        >
                          <Ionicons name="trash-outline" size={18} color={theme.error} />
                        </Pressable>
                      )}
                    </View>
                    <ThemedText style={[styles.commentText, { color: theme.text }]}>
                      {comment.text}
                    </ThemedText>
                  </View>
                );
              })
            )}
          </View>
          
          {/* Spacer for keyboard */}
          <View style={{ height: 120 }} />
        </ScrollView>

        {/* Comment Input */}
        <View style={[styles.inputContainer, { backgroundColor: theme.background, borderTopColor: theme.border }]}>
          <TextInput
            style={[styles.input, { 
              backgroundColor: theme.backgroundSecondary, 
              color: theme.text,
              borderColor: theme.border,
              borderWidth: 1,
            }]}
            placeholder="Add a comment..."
            placeholderTextColor={theme.textTertiary}
            value={commentText}
            onChangeText={setCommentText}
            multiline
            maxLength={500}
          />
          <Pressable 
            onPress={handleAddComment}
            disabled={!commentText.trim() || submitting}
            style={[
              styles.sendButton,
              { backgroundColor: theme.primary }
            ]}
          >
            {submitting ? (
              <ActivityIndicator size="small" color={theme.background} />
            ) : (
              <Ionicons 
                name="send" 
                size={20} 
                color={theme.background} 
              />
            )}
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  commentsSection: {
    paddingTop: 32,
    marginTop: 8,
  },
  commentsTitle: {
    fontSize: 16,
    paddingHorizontal: 20,
    marginBottom: 16,
  },
  loadingContainer: {
    padding: 32,
    alignItems: 'center',
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
  },
  comment: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  commentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  commentUser: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  commentAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  commentUserInfo: {
    flex: 1,
  },
  commentUserName: {
    fontSize: 14,
  },
  commentTime: {
    fontSize: 12,
    marginTop: 2,
  },
  commentText: {
    fontSize: 14,
    lineHeight: 20,
    marginLeft: 40,
  },
  commentDeleteButton: {
    padding: 4,
    marginLeft: 12,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
  },
  input: {
    flex: 1,
    borderRadius: 24,
    paddingHorizontal: 18,
    paddingVertical: 12,
    fontSize: 15,
    maxHeight: 100,
    marginRight: 12,
    minHeight: 48,
  },
  sendButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default PostDetailScreen;
